import { Manifest } from '../../internal/Manifest';
import { Track } from '../Track';
import { TrackInfo, TrackType } from '@quickplay/player-interface';
import { Logger } from '@quickplay/foundation';
const LANG_UNDEFINED = 'und';

interface FilteredTextTrackInfo {
  allTracks: any[];
  currentTrack: any;
}

interface LanguageRole {
  originalLanguage: string;
  language: string;
  role: string;
  accessibilityPurpose?: string;
  label?: string | null;
}

export class ShakaManifestAdapter implements Manifest {
  private availableAudioMimeTypes_: Set<string>;

  constructor(
    private readonly shakaPlayer_: any,
    private readonly logger_: Logger,
  ) {
    this.availableAudioMimeTypes_ = new Set<string>();
  }

  /**
   * Get the manifest that the player has loaded. If the player has not loaded any
   * content, this will return |null|.
   *
   * @description
   * <p>
   * A Manifest object describes a collection of streams (segmented audio, video,
   * or text data) that share a common timeline. We call the collection of
   * streams "the presentation" and their timeline "the presentation timeline".
   * A Manifest describes one of two types of presentations: live and
   * video-on-demand.
   * </p>
   */
  public getPlayerManifest(): any {
    return this.shakaPlayer_.getManifest();
  }

  /**
   * Returns a list of tracks that can be switched in the current
   * period. If there are multiple periods, you must seek to the period in order
   * to get variants from that period.
   *
   * If the player has not loaded content, this will return an empty list.
   *
   * @return TrackInfo[]
   *
   */
  public getAllTracks(): TrackInfo[] {
    // Try new API first (Shaka >=4.14)
    if (
      typeof this.shakaPlayer_.getAudioTracks === 'function' &&
      typeof this.shakaPlayer_.getTextTracks === 'function'
    ) {
      this.logger_.debug('Using new Shaka >=4.14 APIs to get all tracks');
      const audioTracks = this.toUniqueTrackInfoList_(this.shakaPlayer_.getAudioTracks(), TrackType.AUDIO);
      const textTracks = this.toUniqueTrackInfoList_(this.shakaPlayer_.getTextTracks(), TrackType.TEXT);

      // Extract audio codecs from tracks (replaces manifest preprocessor)
      this.extractAudioCodecsFromTracks_(audioTracks);

      return [...audioTracks, ...textTracks];
    }

    // Fallback to old API if the shaka player does not support new >=4.14.x APIs
    this.logger_.debug('Using legacy Shaka APIs for backward compatibility');
    const tracks: TrackInfo[] = [];

    // Get audio tracks from variant tracks (legacy approach)
    for (const track of this.getAvailableAudioLanguagesAndRoles()) {
      tracks.push({
        language: track?.language,
        originalLanguage: track?.originalLanguage,
        type: TrackType.AUDIO,
        accessibilityPurpose: track?.accessibilityPurpose || '',
        role: track?.role,
        label: track?.label || null,
      });
    }

    // Get text tracks with deduplication
    tracks.push(...this.toUniqueTrackInfoList_(this.shakaPlayer_.getTextTracks(), TrackType.TEXT));

    return tracks;
  }

  /**
   * Transforms raw Shaka tracks to TrackInfo objects with deduplication.
   * Maps all available metadata fields and filters to unique tracks.
   * @param rawTracks - Raw tracks from Shaka player APIs
   * @param trackType - Type of track (AUDIO or TEXT)
   * @returns Deduplicated TrackInfo[] with all available metadata
   */
  private readonly toUniqueTrackInfoList_ = (rawTracks: any[] | undefined, trackType: TrackType): TrackInfo[] => {
    const normalizedTracks = Array.isArray(rawTracks) ? rawTracks : [];

    const mappedTracks = normalizedTracks.map(
      (track: any): TrackInfo => ({
        type: trackType,
        language: track.language || LANG_UNDEFINED,
        originalLanguage: track.originalLanguage,
        kind: track.kind,
        accessibilityPurpose: track.accessibilityPurpose,
        role: track.roles?.[0] || track.role,
        roles: track.roles,
        label: track.label ?? null,
        active: track.active,
        audioSamplingRate: track.audioSamplingRate ?? null,
        channelsCount: track.channelsCount ?? null,
        codecs: track.codecs,
        mimeType: track.mimeType,
        primary: track.primary,
        spatialAudio: track.spatialAudio,
        bandwidth: track.bandwidth,
        forced: track.forced,
        id: track.id,
        hdr: track.hdr,
        originalTextId: track.originalTextId,
        originalAudioId: track.originalAudioId,
      }),
    );

    // Filter to unique tracks based on combination key
    return this.getUniqueTracksByKey_(mappedTracks, trackType);
  };

  /**
   * Filters tracks to unique entries based on their combination key.
   * For Audio tracks: Uses originalAudioId, or falls back to language + roles + spatialAudio + channelsCount + label
   * For Text tracks: Uses originalTextId + kind, or falls back to language + roles + label + kind
   * Keeps the first occurrence of each unique track combination.
   * @param tracks - Array of TrackInfo objects
   * @param trackType - Type of track (AUDIO or TEXT)
   * @returns Unique TrackInfo[]
   */
  private readonly getUniqueTracksByKey_ = (tracks: TrackInfo[], trackType: TrackType): TrackInfo[] => {
    const combinationsMade = new Set<string>();
    const result: TrackInfo[] = [];

    for (const track of tracks) {
      const combination = this.getTrackCombinationKey_(track, trackType);
      if (!combinationsMade.has(combination)) {
        combinationsMade.add(combination);
        result.push(track);
      }
    }

    return result;
  };

  /**
   * Gets the combination key for a track based on its type and identifying properties.
   * Audio: originalAudioId OR fallback (language + roles + spatialAudio + channelsCount + label)
   * Text: originalTextId + kind OR fallback (language + roles + label + kind)
   * @param track - TrackInfo object
   * @param trackType - Type of track (AUDIO or TEXT)
   * @returns Combination key string
   */
  private readonly getTrackCombinationKey_ = (track: TrackInfo, trackType: TrackType): string => {
    // Prefer original IDs from Shaka as they uniquely identify source tracks
    if (trackType === TrackType.AUDIO && track.originalAudioId !== undefined) {
      return `audio_${track.originalAudioId}`;
    }
    const kind = track.kind ?? '';
    if (trackType === TrackType.TEXT && track.originalTextId !== undefined && track.originalTextId !== null) {
      return `text_${track.originalTextId}_${kind}`;
    }

    // Fallback to combination-based key (similar to UI track selection logic)
    const language = track.originalLanguage || track.language || LANG_UNDEFINED;
    const rolesString = track.roles?.slice().sort().join(',') || track.role || '';
    const label = track.label || '';

    if (trackType === TrackType.AUDIO) {
      // Audio: language + roles + spatialAudio + channelsCount + label
      const spatialAudio = track.spatialAudio ?? false;
      const channelsCount = track.channelsCount ?? '';
      return `audio_${language}_${rolesString}_${spatialAudio}_${channelsCount}_${label}`;
    }

    // Text: language + roles + label + kind
    return `text_${language}_${rolesString}_${label}_${kind}`;
  };

  /**
   * Extracts unique audio codecs from audio tracks and updates the internal codec set.
   * This replaces the deprecated manifest preprocessor logic.
   *
   * Codec Transformation:
   * - ec-3 with spatialAudio=true → 'eac3-joc' (Dolby Atmos)
   * - ec-3 with spatialAudio=false → 'eac3' (Dolby Digital Plus)
   * - Other codecs → as-is
   *
   * @param audioTracks - Array of audio TrackInfo objects
   */
  private readonly extractAudioCodecsFromTracks_ = (audioTracks: TrackInfo[]): void => {
    if (this.availableAudioMimeTypes_.size > 0) {
      return; // Already populated
    }

    const codecs = new Set<string>();
    for (const track of audioTracks) {
      if (track.codecs) {
        const normalizedCodec = this.normalizeAudioCodec_(track.codecs, track.spatialAudio);
        codecs.add(normalizedCodec);
      }
    }

    if (codecs.size > 0) {
      this.logger_.debug('Extracted audio codecs from tracks:', codecs);
      this.setAudioCodecs(codecs);
    }
  };

  /**
   * Normalizes audio codec based on codec type and spatial audio flag.
   * - ec-3 with spatialAudio=true → 'eac3-joc' (Dolby Atmos)
   * - ec-3 with spatialAudio=false → 'eac3' (Dolby Digital Plus)
   * - Other codecs → returned as-is
   *
   * @param codec - The codec string from the track
   * @param spatialAudio - Whether the track has spatial audio
   * @returns Normalized codec string
   */
  private readonly normalizeAudioCodec_ = (codec: string, spatialAudio?: boolean): string => {
    const lowerCodec = codec.toLowerCase();

    if (lowerCodec.includes('ec-3')) {
      return spatialAudio ? 'eac3-joc' : 'eac3';
    }

    return codec;
  };

  /**
   * Returns a list of active tracks in the current
   * period.
   *
   * If the player has not loaded content, this will return an empty list.
   *
   * @return TrackInfo[]
   */
  public getActiveTracks(): TrackInfo[] {
    const activeTracks: TrackInfo[] = [];

    // Try new API first (Shaka >=4.14)
    if (typeof this.shakaPlayer_.getAudioTracks === 'function') {
      const activeAudioTrack = this.shakaPlayer_.getAudioTracks().find((track: any) => track.active);
      if (activeAudioTrack) {
        activeTracks.push(this.toUniqueTrackInfoList_([activeAudioTrack], TrackType.AUDIO)[0]);
      }
    } else {
      // Fallback to variant tracks for older Shaka versions
      const activeAudioTrack = this.shakaPlayer_.getVariantTracks().find((track: any) => track.active);
      if (activeAudioTrack) {
        activeTracks.push({
          language: activeAudioTrack.language,
          originalLanguage: activeAudioTrack?.originalLanguage,
          role: activeAudioTrack?.audioRoles?.[0] ?? '',
          roles: activeAudioTrack?.audioRoles,
          accessibilityPurpose: activeAudioTrack?.accessibilityPurpose || '',
          label: activeAudioTrack?.label || null,
          type: TrackType.AUDIO,
          active: true,
        });
      }
    }

    // Text tracks API is the same for both old and new Shaka versions
    const activeTextTrack = this.shakaPlayer_.getTextTracks().find((track: any) => track.active);
    if (activeTextTrack) {
      if (typeof this.shakaPlayer_.getAudioTracks === 'function') {
        // Use new mapping for consistency
        activeTracks.push(this.toUniqueTrackInfoList_([activeTextTrack], TrackType.TEXT)[0]);
      } else {
        activeTracks.push({
          language: activeTextTrack.language || LANG_UNDEFINED,
          originalLanguage: activeTextTrack?.originalLanguage || LANG_UNDEFINED,
          type: TrackType.TEXT,
          active: true,
        });
      }
    }

    return activeTracks;
  }

  /**
   * Select a specific variant track to play from the current period. |track|
   * should come from a call to |getAllTracks|. If |track| cannot be found
   * in the current variant, this will be a no-op. If the player has not loaded
   * content, this will be a no-op.
   *
   * Changing variants will take effect once the currently buffered content has
   * been played.
   *
   * Note that AdaptationEvents are not fired for manual track selections.
   *
   * @param {TrackInfo | undefined}
   */
  public selectTrack(track?: TrackInfo): void {
    if (!track) {
      this.shakaPlayer_.setTextTrackVisibility(false);
      return;
    }

    if (track.type === TrackType.TEXT) {
      this.selectTextLanguage_(track);
    } else if (track.type === TrackType.AUDIO) {
      this.selectAudioLanguage_(track);
    } else {
      this.logger_.warn(`Unhandled track type: ${track?.type}`);
    }
  }

  /**
   * Sets the minimum bandwidth of a variant track, in bit/sec.
   *
   * @param minBandwidth
   */
  public setMinBandwidth(minBandwidth: number): void {
    this.logger_.debug(`Updating min bandwidth to: ${minBandwidth}`);

    this.shakaPlayer_.configure({
      abr: {
        restrictions: {
          minBandwidth: minBandwidth === 0 ? undefined : minBandwidth,
        },
      },
    });
  }

  /**
   * Gets the minimum bandwidth of a variant track, in bit/sec.
   *
   * @returns {number} bandwidth
   */
  public getMinBandwidth(): number {
    const playerRestrictions = this.shakaPlayer_.getConfiguration().abr.restrictions;

    return playerRestrictions.minBandwidth;
  }

  /**
   * Sets the maximum bandwidth of a variant track, in bit/sec.
   *
   * @param maxBandwidth
   */
  public setMaxBandwidth(maxBandwidth: number): void {
    this.logger_.debug(`Updating max bandwidth to: ${maxBandwidth}`);

    this.shakaPlayer_.configure({
      abr: {
        restrictions: {
          maxBandwidth: maxBandwidth === 0 ? undefined : maxBandwidth,
        },
      },
    });
  }

  /**
   * Gets the maximum bandwidth of a variant track, in bit/sec.
   *
   * @return {number} maxBandwidth
   */
  public getMaxBandwidth(): number {
    const playerRestrictions = this.shakaPlayer_.getConfiguration().abr.restrictions;

    return playerRestrictions.maxBandwidth;
  }

  /**
   * Set Playback Rate to skip through content without playing by repeatedly
   * seeking. For example, a rate of 2.5 would result in 2.5 seconds of content
   * being skipped every second. A negative rate will result in moving backwards.
   *
   * If the player has not loaded content or is still loading content this will be
   * a no-op. Wait until |load| has completed before calling.
   *
   * Trick play will be canceled automatically if the playhead hits the beginning
   * or end of the seekable range for the content.
   *
   * @param {number} rate
   */
  public setPlaybackRate(rate: number): void {
    this.logger_.debug(`Setting playback rate to ${rate}`);

    this.shakaPlayer_.trickPlay(rate, false);
  }

  /**
   * Get the playback rate of what is playing right now. If we are using trick
   * play, this will return the trick play rate. If no content is playing, this
   * will return 0. If content is buffering, this will return 0.
   *
   * If the player has not loaded content, this will return a playback rate of
   * |0|.
   *
   * @return {number}
   */
  public getPlaybackRate(): number {
    return this.shakaPlayer_.getPlaybackRate();
  }

  /**
   * A default clock sync URI to be used with live streams which do not contain any clock sync information.
   * The Date header from this URI will be used to determine the current time.
   * It is a simple URI attached with http-head:2014 scheme.
   *
   * @param {string} clockSyncUri
   *
   * Note: Clock sync is critical for live playback, as the client must know what segments are available when.
   * An individual client's clock can be potentially very far off, but even small inaccuracies can have a big impact on playback.
   *
   * If the UTCTiming element is present in the manifest, the client can synchronize its clock by that.
   * If not, the configured clockSyncUri will be used as a fallback
   */
  public setClockSyncUri(clockSyncUri: string): void {
    this.shakaPlayer_.configure({
      manifest: {
        dash: {
          clockSyncUri: clockSyncUri,
        },
      },
    });
  }

  public getClockSyncUri(): string {
    return this.shakaPlayer_.getConfiguration().manifest.dash.clockSyncUri;
  }

  /**
   * The maximum number of seconds of content that the StreamingEngine will keep
   * in buffer behind the playhead when it appends a new media segment.
   *
   * @param {number} amount
   */
  public setBufferBehind(amount: number): void {
    this.logger_.debug(`Updating bufferBehind to: ${amount}`);

    this.shakaPlayer_.configure({
      streaming: {
        bufferBehind: amount,
      },
    });
  }

  /**
   * Enables media-segment inspection for HLS playlists that omit CODECS
   * metadata, allowing Shaka to detect MIME type and codec information.
   *
   * @param disableCodecGuessingForHls
   */
  public setDisableCodecGuessingForHls(disableCodecGuessingForHls: boolean): void {
    this.logger_.debug(`Updating HLS media-info detection from segments to: ${disableCodecGuessingForHls}`);

    this.shakaPlayer_.configure({
      manifest: {
        hls: {
          disableCodecGuessing: disableCodecGuessingForHls,
        },
      },
    });
  }

  /**
   * It allows player config to take precedence over manifest for ignoreSuggestedPresentationDelay.
   * If true will cause DASH parser to ignore the ignoreSuggestedPresentationDelay attribute from the manifest.
   * Defaults to false if not provided.
   * @param {boolean} ignoreSuggestedPresentationDelay
   */
  public setIgnoreSuggestedPresentationDelay(ignoreSuggestedPresentationDelay: boolean): void {
    this.logger_.debug(`Updating ignoreSuggestedPresentationDelay to: ${ignoreSuggestedPresentationDelay}`);

    this.shakaPlayer_.configure({
      manifest: {
        dash: {
          ignoreSuggestedPresentationDelay: ignoreSuggestedPresentationDelay,
        },
      },
    });
  }

  /**
   * A default presentationDelay value. For DASH, it's a default presentationDelay value
   * if suggestedPresentationDelay is missing in the MPEG DASH manifest. The default value is 1.5 * minBufferTime
   * if not configured or set as 0. For HLS, the default value is 3 segments duration if not configured or set as 0.
   * @param {number} defaultPresentationDelay
   */
  public setDefaultPresentationDelay(defaultPresentationDelay: number): void {
    this.logger_.debug(`Updating defaultPresentationDelay to: ${defaultPresentationDelay}`);

    this.shakaPlayer_.configure({
      manifest: {
        defaultPresentationDelay: defaultPresentationDelay,
      },
    });
  }

  /**
   * The default presentation delay will be calculated as a number of segments.
   * This is the number of segments for this calculation, Defaults to 3.
   * @param {number} hlsLiveSegmentsDelay
   */
  public setHlsLiveSegmentsDelay(hlsLiveSegmentsDelay: number): void {
    this.logger_.debug(`Updating Hls Live Segments Delay to: ${hlsLiveSegmentsDelay}`);

    this.shakaPlayer_.configure({
      manifest: {
        hls: {
          liveSegmentsDelay: hlsLiveSegmentsDelay,
        },
      },
    });
  }

  /**
   * It allows player config to take precedence over manifest for rebufferingGoal.
   * If true will cause DASH parser to ignore minBufferTime from manifest.
   * Defaults to false if not provided.
   * @param {boolean} ignoreMinBufferTime
   */
  public setIgnoreMinBufferTime(ignoreMinBufferTime: boolean): void {
    this.logger_.debug(`Updating Ignore Min Buffer Time to: ${ignoreMinBufferTime}`);

    this.shakaPlayer_.configure({
      manifest: {
        dash: {
          ignoreMinBufferTime: ignoreMinBufferTime,
        },
      },
    });
  }

  /**
   * It allows player config to take precedence over manifest for segmentRelativeVttTiming
   * the Option to calculate VTT text timings relative to the segment start instead of relative to the period start
   * Defaults to false.
   * @param {boolean} segmentRelativeVttTiming
   */
  public setSegmentRelativeVttTiming(segmentRelativeVttTiming: boolean): void {
    this.logger_.debug(`Updating Segment Relative Vtt Timing to: ${segmentRelativeVttTiming}`);

    this.shakaPlayer_.configure({
      manifest: {
        segmentRelativeVttTiming: segmentRelativeVttTiming,
      },
    });
  }

  /**
   * It allows player config to enable autoCorrectDrift over dash manifest.
   * If true, ignore the availabilityStartTime in the manifest and instead use the segments to determine the live edge.
   * This allows us to play streams that have a lot of drift. If false, we can't play content where the manifest specifies segments in the future.
   * Defaults to true.
   * @param enableAutoCorrectDrift
   */
  public setAutoCorrectDrift(enableAutoCorrectDrift: boolean): void {
    this.logger_.debug(`updating autoCorrectDrift to: ${enableAutoCorrectDrift}`);

    this.shakaPlayer_.configure({
      manifest: {
        dash: {
          autoCorrectDrift: enableAutoCorrectDrift,
        },
      },
    });
  }

  /**
   * If this is true,
   * Playback will set the availability window to the presentation delay.
   * The player will be able to buffer ahead three segments, but the seek window will be zero-sized, to be consistent with Safari.
   * If this is false, the seek window will be the entire duration. Defaults to true.
   * Note: Deprecated in Shaka version 4.11.0.
   * @param safariBehaviourForLiveHls
   */
  public setSafariBehaviourForLiveHls(safariBehaviourForLiveHls: boolean): void {
    this.logger_.debug(`updating safariBehaviourForLiveHls to: ${safariBehaviourForLiveHls}`);

    this.shakaPlayer_.configure({
      manifest: {
        hls: {
          useSafariBehaviorForLive: safariBehaviourForLiveHls,
        },
      },
    });
  }

  /**
   * @property {boolean} enableAudioGroups
   * If set, audio streams will be grouped and filtered by their parent adaptation set ID.
   * Defaults to true.
   * @param enableAudioGroups
   */
  public setEnableAudioGroups(enableAudioGroups: boolean): void {
    this.logger_.debug(`updating enableAudioGroups to: ${enableAudioGroups}`);

    this.shakaPlayer_.configure({
      manifest: {
        enableAudioGroups: enableAudioGroups,
      },
    });
  }

  public getBufferBehind(): number {
    return this.shakaPlayer_.getConfiguration().streaming.bufferBehind;
  }

  /**
   *  The number of seconds of content that the StreamingEngine will attempt to
   *  buffer ahead of the playhead. This value must be greater than or equal to
   *  the minBuffer.
   *
   * @param maxBuffer
   */
  public setMaxBuffer(maxBuffer: number): void {
    this.logger_.debug(`Updating maxBuffer to: ${maxBuffer}`);
    this.shakaPlayer_.configure({
      streaming: {
        bufferingGoal: maxBuffer,
      },
    });
  }

  public getMaxBuffer(): number {
    return this.shakaPlayer_.getConfiguration().streaming.bufferingGoal;
  }

  /**
   * The number of seconds the content has to be buffered before playback
   * starts. This affects both buffering at startup and re-buffering later
   * during playback.
   *
   * For example, if this is set to 15,  buffering state remains until the
   * content is 15 seconds buffered.
   *
   * Note:
   * 1. Min Buffer should always be less than Max Buffer.
   * 2. If DASH manifest's minBufferTime is greater it overrides Min Buffer.
   *
   * @param minBuffer
   */
  public setMinBuffer(minBuffer: number): void {
    this.logger_.debug(`Updating minBuffer to: ${minBuffer}`);

    this.shakaPlayer_.configure({
      streaming: {
        rebufferingGoal: minBuffer,
      },
    });
  }

  public getMinBuffer(): number {
    return this.shakaPlayer_.getConfiguration().streaming.rebufferingGoal;
  }

  public getAvailableBitrates(): number[] {
    return this.shakaPlayer_
      .getVariantTracks()
      .reduce((bitrates: number[], track: any) => {
        if (!bitrates.includes(track.bandwidth)) {
          bitrates.push(track.bandwidth);
        }

        return bitrates;
      }, [])
      .sort((a: number, b: number) => {
        if (a < b) return -1;
        if (a > b) return 1;

        return 0;
      }); // They're usually in order, but just in case...
  }

  /**
   * Below APIs are not exposed. Upon request from client team these would be exposed.
   */

  /**
   * Return a list of audio languages available for the current period. If the
   * player has not loaded any content, this will return an empty list.
   *
   * @return {!Array.<string>}
   */
  public getAvailableAudioLanguages(): string[] {
    // Use backward compatible utility function
    return this.getAudioLanguages(this.shakaPlayer_);
  }

  /**
   * Return a list of audio language-role combinations available for the current
   * period. If the player has not loaded any content, this will return an empty
   * list.
   *
   * LanguageRole
   * @typedef {{
   *   language: string,
   *   originalLanguage: string,
   *   role: string,
   *   accessibilityPurpose: string,
   *   label: string | null,
   * }}
   * @return {Array.<LanguageRole>}
   */
  public getAvailableAudioLanguagesAndRoles(): LanguageRole[] {
    const audioLangRoles = this.getAudioLanguagesAndRoles(this.shakaPlayer_);
    if (audioLangRoles.length === 0) {
      return [];
    }

    // Create a map of language to originalLanguage from variant tracks
    const languageMap = new Map<string, any>();
    const variantTracks: any[] = this.shakaPlayer_.getVariantTracks();

    variantTracks.forEach((track: any) => {
      if (track.language && track.originalLanguage) {
        const language = track?.language || track?.originalLanguage;
        const key = `${language}_${track.roles?.[0]}`;
        languageMap.set(key, track);
      }
    });

    // Enhance the basic language list with track metadata
    return audioLangRoles.map((langInfo: any): LanguageRole => {
      const trackKey = `${langInfo.language}_${langInfo.role}`;
      const trackInfo = languageMap.get(trackKey);

      return {
        language: langInfo.language,
        originalLanguage: trackInfo?.originalLanguage || '',
        role: langInfo.role || '',
        accessibilityPurpose: trackInfo?.accessibilityPurpose || '',
        label: trackInfo?.label || null,
      };
    });
  }

  /**
   * Returns the current Audio Language. If the player has not loaded any
   * content, this will be empty string.
   *
   * @param {string} language
   * @param {string=} role
   *
   */
  public getAudioLanguage(): string {
    const activeTrack = this.shakaPlayer_.getVariantTracks().find((track: any) => track.active);

    return activeTrack ? activeTrack.language : '';
  }

  /**
   * Returns the current Audio Language and current audio Role. If the player has not loaded any
   * content, this will be empty list.
   *
   * @param {string} language
   * @param {string=} role
   *
   */
  public getAudioLanguageAndRole(): LanguageRole {
    const activeTrack = this.shakaPlayer_.getVariantTracks().find((track: any) => track.active);
    return {
      language: activeTrack ? activeTrack.language : '',
      originalLanguage: activeTrack ? activeTrack.originalLanguage : '',
      role: activeTrack ? activeTrack.audioRoles : '',
    };
  }

  /**
   * Return a list of text languages available for the current period. If the
   * player has not loaded any content, this will return an empty list.
   *
   * @return {!Array.<string>}
   *
   */
  public getAvailableTextLanguages(): string[] {
    return this.shakaPlayer_.getTextLanguages();
  }

  /**
   * Return a list of text language-role combinations available for the current
   * period. If the player has not loaded any content, this will be return an
   * empty list.
   *
   * @return {!Array.<LanguageRole>}
   *
   */
  public getAvailableTextLanguagesAndRoles(): LanguageRole[] {
    return this.shakaPlayer_.getTextLanguagesAndRoles();
  }

  /**
   * Return the active text language. If the player has not loaded any content, this will be return an
   * empty string.
   *
   * @return {string}
   */
  public getTextLanguage(): string {
    const activeTrack = this.shakaPlayer_.getTextTracks().find((track: any) => track.active);

    return activeTrack ? activeTrack.language : '';
  }

  /**
   * Return the active text language-role combinations. If the player has not loaded any content,
   * this will be return an empty list.
   *
   * @return {LanguageRole}
   */
  public getTextLanguageAndRole(): LanguageRole {
    const activeTrack = this.shakaPlayer_.getTextTracks().find((track: any) => track.active);
    return {
      language: activeTrack ? activeTrack.language : '',
      originalLanguage: activeTrack ? activeTrack.originalLanguage : '',
      role: activeTrack ? activeTrack.roles : '',
    };
  }

  /**
   * Sets currentTextLanguage and currentTextRole to the selected language and
   * role, and chooses a new variant if need be. If the player has not loaded any
   * content, this will be a no-op.
   *
   * @param {string} language
   * @param {string=} role
   * @export
   */
  public setTextLanguage(language: string, role?: string): void {
    this.logger_.debug(`Setting text language to ${language} and ${role}`);

    this.shakaPlayer_.selectTextLanguage(language, role);
  }

  /**
   * Return a list of text tracks that can be switched to in the current period.
   * If there are multiple periods, you must seek to a period in order to get
   * text tracks from that period.
   *
   * If the player has not loaded content, this will return an empty list.
   *
   * @return {!Array.<Track>}
   * @export
   */
  public getAvailableTextTracks(): Track[] {
    return this.filterTextTracks_(this.shakaPlayer_.getTextTracks()).allTracks;
  }

  /**
   * Return the active text track that can be switched to in the current period.
   * If the player has not loaded content, this will return an empty track.
   *
   * @return <Track>
   * @export
   */
  public getCurrentTextTrack(): Track {
    return this.filterTextTracks_(this.shakaPlayer_.getTextTracks()).currentTrack;
  }

  /**
   * Select a specific text track from the current period. |track| should come
   * from a call to |getTextTracks|. If the track is not found in the current
   * period, this will be a no-op. If the player has not loaded content, this will
   * be a no-op.
   *
   * Note that AdaptationEvents are not fired for manual track selections.
   *
   * @param {Track} track
   * @export
   */
  public setTextTrack(track: Track): void {
    const trackId = Number(track.id);
    const shakaTextTracks = this.shakaPlayer_.getTextTracks();
    const textTrack = shakaTextTracks.find((shakaTextTrack: any) => shakaTextTrack.id === trackId);

    if (!textTrack) {
      this.logger_.debug(`No track with id = ${trackId}`);
    } else {
      this.logger_.debug(`Selecting text track id = ${trackId}`);
      this.shakaPlayer_.selectTextTrack(textTrack);
    }
  }

  private readonly filterTextTracks_ = (tracks: any[] = []): FilteredTextTrackInfo => {
    const returnVal: FilteredTextTrackInfo = {
      allTracks: [],
      currentTrack: undefined,
    };

    returnVal.allTracks = tracks.map((track: any) => {
      const filteredTrack = {
        id: track.id,
        type: 'text',
        language: track.language,
        kind: track.kind,
        display: track.enabled,
      };

      if (track.active) {
        returnVal.currentTrack = filteredTrack;
      }

      return filteredTrack;
    });

    return returnVal;
  };

  public setRaiseFatalErrorOnManifestUpdateRequestFailure(shouldRaiseFatalError: boolean): void {
    this.logger_.debug(`Updating raiseFatalErrorOnManifestUpdateRequestFailure to: ${shouldRaiseFatalError}`);

    this.shakaPlayer_.configure({
      manifest: {
        raiseFatalErrorOnManifestUpdateRequestFailure: shouldRaiseFatalError,
      },
    });
  }

  /**
   * Normalizes a raw Shaka track object to our TrackInfo interface.
   * Extracts and maps relevant properties for consistent track comparison.
   * @param rawTrack - Raw track object from Shaka player APIs
   * @param trackType - Type of track (AUDIO or TEXT)
   * @returns Normalized TrackInfo object
   */
  private readonly normalizeToTrackInfo_ = (rawTrack: any, trackType: TrackType): TrackInfo => {
    const baseTrackInfo: TrackInfo = {
      type: trackType,
      language: rawTrack.language || LANG_UNDEFINED,
      originalLanguage: rawTrack.originalLanguage,
      role: rawTrack.roles?.[0] || rawTrack.role,
      roles: rawTrack.roles,
      label: rawTrack.label ?? null,
    };

    if (trackType === TrackType.AUDIO) {
      return {
        ...baseTrackInfo,
        spatialAudio: rawTrack.spatialAudio,
        channelsCount: rawTrack.channelsCount ?? null,
        originalAudioId: rawTrack.originalAudioId,
      };
    }

    // TEXT track
    return {
      ...baseTrackInfo,
      kind: rawTrack.kind,
      originalTextId: rawTrack.originalTextId,
    };
  };

  /**
   * Finds a raw Shaka track that matches the given TrackInfo.
   *
   * Matching Strategy (in order of priority):
   * 1. Exact combination key match (originalAudioId/originalTextId or full properties)
   * 2. Language-based match (compares both language and originalLanguage)
   *
   * This flexible matching allows users to select tracks using minimal properties
   * (e.g., just language) while still finding the correct track.
   *
   * @param rawTracks - Array of raw tracks from Shaka player APIs
   * @param targetTrack - TrackInfo to match against
   * @param trackType - Type of track (AUDIO or TEXT)
   * @returns Matching raw track or undefined if not found
   */
  private readonly findMatchingTrack_ = (rawTracks: any[], targetTrack: TrackInfo, trackType: TrackType): any => {
    const targetKey = this.getTrackCombinationKey_(targetTrack, trackType);

    // First try exact combination key match
    const exactMatch = rawTracks.find((rawTrack: any) => {
      const trackInfo = this.normalizeToTrackInfo_(rawTrack, trackType);
      return this.getTrackCombinationKey_(trackInfo, trackType) === targetKey;
    });

    if (exactMatch) {
      return exactMatch;
    }

    // Fallback: Match by language (handles cases where user provides minimal track info)
    const targetLanguage = targetTrack.originalLanguage || targetTrack.language;
    if (targetLanguage) {
      return rawTracks.find((rawTrack: any) => {
        const matchesLanguage = rawTrack.language === targetLanguage || rawTrack.originalLanguage === targetLanguage;
        const matchesRole =
          !targetTrack.role || rawTrack.roles?.includes(targetTrack.role) || rawTrack.role === targetTrack.role;
        return matchesLanguage && matchesRole;
      });
    }

    return undefined;
  };

  /**
   * Selects an audio track with backward compatibility for different Shaka versions.
   *
   * Selection Strategy:
   * 1. New API (Shaka >=4.14): Uses selectAudioTrack(track) with combination key matching
   * 2. Legacy API (Shaka <4.14): Falls back to selectAudioLanguage(language, role)
   *
   * @param track - TrackInfo object from getAllTracks()
   */
  private readonly selectAudioLanguage_ = (track: TrackInfo): void => {
    const language = track?.originalLanguage || track?.language;
    this.logger_.debug(`Selecting audio track: language=${language}, role=${track?.role}, label=${track?.label}`);

    // New API (Shaka >=4.14): Use selectAudioTrack with combination key matching
    if (typeof this.shakaPlayer_?.selectAudioTrack === 'function') {
      const audioTracks = this.shakaPlayer_.getAudioTracks();
      const matchingTrack = this.findMatchingTrack_(audioTracks, track, TrackType.AUDIO);

      if (matchingTrack) {
        this.logger_.debug(`Selecting audio track via new API (id=${matchingTrack.id})`);
        this.shakaPlayer_.selectAudioTrack(matchingTrack);
      } else {
        this.logger_.warn('Audio track not found in available tracks');
      }
      return;
    }

    // Legacy API (Shaka <4.14): Use selectAudioLanguage
    if (typeof this.shakaPlayer_?.selectAudioLanguage === 'function') {
      if (!language) {
        this.logger_.warn('No language available for track selection');
        return;
      }
      this.logger_.debug(`Selecting audio language via legacy API: ${language}, role=${track?.role}`);
      this.shakaPlayer_.selectAudioLanguage(language, track?.role);
      return;
    }

    this.logger_.warn('No audio selection method available');
  };

  /**
   * Selects a text track with backward compatibility for different Shaka versions.
   *
   * Selection Strategy:
   * 1. New API (Shaka >=4.14): Uses selectTextTrack(track) with combination key matching
   * 2. Legacy API (Shaka <4.14): Falls back to selectTextLanguage(language, role)
   *
   * Note: Text track visibility is automatically enabled after selection.
   *
   * @param track - TrackInfo object from getAllTracks()
   */
  private readonly selectTextLanguage_ = (track: TrackInfo): void => {
    const language = track?.originalLanguage || track?.language;
    this.logger_.debug(`Selecting text track: language=${language}, role=${track?.role}, label=${track?.label}`);

    const textTracks = this.shakaPlayer_.getTextTracks();
    const matchingTrack = this.findMatchingTrack_(textTracks, track, TrackType.TEXT);

    // New API (Shaka >=4.14): Use selectTextTrack with combination key matching
    if (typeof this.shakaPlayer_?.selectTextTrack === 'function' && matchingTrack) {
      this.logger_.debug(`Selecting text track via new API (id=${matchingTrack.id})`);
      this.shakaPlayer_.selectTextTrack(matchingTrack);
      this.shakaPlayer_.setTextTrackVisibility(true);
      return;
    }

    // Legacy API (Shaka <4.14): Use selectTextLanguage
    if (typeof this.shakaPlayer_?.selectTextLanguage === 'function') {
      this.selectTextLanguageViaLegacyApi_(track, textTracks);
      return;
    }

    this.logger_.warn('No text selection method available');
  };

  /**
   * Handles text language selection using the legacy Shaka API (<4.14).
   * Includes special handling for undefined language tracks.
   * @param track - TrackInfo to select
   * @param textTracks - Available text tracks from Shaka player
   */
  private readonly selectTextLanguageViaLegacyApi_ = (track: TrackInfo, textTracks: any[]): void => {
    const language = track.originalLanguage || track.language;

    // Handle undefined language tracks specially
    if (language === LANG_UNDEFINED) {
      const hasUndefinedTrack = textTracks.some((t: any) => t.language === LANG_UNDEFINED);
      this.shakaPlayer_.selectTextLanguage(hasUndefinedTrack ? LANG_UNDEFINED : '');
    } else if (language) {
      this.logger_.debug(`Selecting text language via legacy API: ${language}, role=${track?.role}`);
      this.shakaPlayer_.selectTextLanguage(language, track?.role);
    }

    this.shakaPlayer_.setTextTrackVisibility(true);
  };

  /**
   * Set the available audio codecs
   * @param codecs
   * @returns void
   */
  private setAudioCodecs(codecs: Set<string>): void {
    this.availableAudioMimeTypes_ = codecs;
  }

  /**
   * Get the available audio codecs
   * @returns Set<string>
   */
  public getAvailableAudioCodecs(): Set<string> {
    return this.availableAudioMimeTypes_;
  }

  /**
   * Gets audio languages with backward compatibility
   * New API: getAudioTracks() - returns track objects, 4.14.x onwards
   * Old API: getAudioLanguages() - returns language strings
   * @param player - Shaka Player instance
   * @returns Array of language strings
   */
  private getAudioLanguages(player: any): string[] {
    // Try new API first
    if (typeof player.getAudioTracks === 'function') {
      const tracks = player.getAudioTracks();
      return tracks
        .map((track: any) => track.originalLanguage || track.language)
        .filter(
          (lang: string, index: number, arr: string[]) => arr.indexOf(lang) === index, // Remove duplicates
        );
    }

    // Fallback to old API
    if (typeof player.getAudioLanguages === 'function') {
      return player.getAudioLanguages();
    }

    return [];
  }

  /**
   * Gets audio languages and roles with backward compatibility
   * New API: getAudioTracks() - returns track objects with language and roles
   * Old API: getAudioLanguagesAndRoles() - returns language-role combinations
   * @param player - Shaka Player instance
   * @returns Array of language-role objects
   */
  private getAudioLanguagesAndRoles(player: any): any[] {
    // Try new API first
    if (typeof player?.getAudioTracks === 'function') {
      const tracks = player.getAudioTracks();
      // Transform track objects to match old API format: language, role, label only
      const transformedTracks = tracks.map((track: any) => ({
        language: track.language || '',
        role: track.roles?.[0] || track.role || '',
        label: track.label || null,
      }));

      // Deduplicate based on language and role combination
      const uniqueTracks = new Map<string, any>();
      transformedTracks.forEach((track: any) => {
        const key = `${track.language}_${track.role}`;
        if (!uniqueTracks.has(key)) {
          uniqueTracks.set(key, track);
        }
      });

      return Array.from(uniqueTracks.values());
    }

    // Fallback to old API
    if (typeof player?.getAudioLanguagesAndRoles === 'function') {
      return player.getAudioLanguagesAndRoles();
    }

    return [];
  }
}
